"use client";

import { useEffect, useRef, useState } from "react";
import { Inter } from "next/font/google";
import hw from "../hoverwords/HoverWord.module.scss";
import { FigTag } from "../hoverwords/FigTag";
import { EXIT_MS } from "../hoverwords/shared";
import styles from "./AboutName.module.scss";

/* Figma's UI font — same instance the hover quotations load; Next dedupes. */
const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500"],
  variable: "--font-figma",
  display: "swap",
  preload: false,
});

const HANDLE = 8;
const SHRINK = 0.94;

/* The touch-up performance, ms from the cursor's first frame: Vik's own
   multiplayer cursor arrives and selects the name, tries the serif on the
   surname and thinks better of it, then checks the size — drags the corner
   in and lets it back out to exactly where it was. The chrome's pop/fade
   timings are the hover-word ones in HoverWord.module.scss. */
const T = {
  click: 1050,
  tryOn: 2150,
  tryOff: 3250,
  grab: 4050,
  dragIn: 4200,
  dragHold: 4720,
  dragOut: 4900,
  settle: 5380,
  deselect: 5900,
  done: 6650,
} as const;

type Ease = (u: number) => number;
const easeOut: Ease = (u) => 1 - (1 - u) ** 3;
const easeInOut: Ease = (u) =>
  u < 0.5 ? 4 * u ** 3 : 1 - (-2 * u + 2) ** 3 / 2;
/* slight overshoot so the drag settles back onto 100% like a hand would */
const backOut: Ease = (u) => {
  const c = 1.4;
  const p = u - 1;
  return 1 + (c + 1) * p ** 3 + c * p ** 2;
};
const seg = (t: number, a: number, b: number) =>
  Math.min(1, Math.max(0, (t - a) / (b - a)));

function scaleAt(t: number) {
  if (t < T.dragIn) return 1;
  if (t < T.dragHold)
    return 1 + (SHRINK - 1) * easeInOut(seg(t, T.dragIn, T.dragHold));
  if (t < T.dragOut) return SHRINK;
  if (t < T.settle)
    return SHRINK + (1 - SHRINK) * backOut(seg(t, T.dragOut, T.settle));
  return 1;
}

/* the arrow dips at click, grab and release — the only "hand" we can show */
function dipAt(t: number) {
  let s = 1;
  for (const at of [T.click, T.grab, T.settle]) {
    const u = (t - at) / 150;
    if (u >= 0 && u <= 1) s *= 1 - 0.12 * Math.sin(Math.PI * u);
  }
  return s;
}

interface Geom {
  w: number;
  h: number;
  selX: number;
  selY: number;
  selW: number;
  selH: number;
}

interface Waypoint {
  t: number;
  x: number;
  y: number;
  ease?: Ease;
}

/* The about-page h1 as a Figma frame Vik is still finishing. Plays once,
   after the head is in view and fonts are settled; the text SSRs plainly
   and never waits on any of this. The serif try and the corner drag are
   real changes to the live heading — the line boxes are fixed and the drag
   is a transform, so the page below never moves. Skipped entirely under
   reduced motion. */
export function AboutName({
  first,
  last,
  className,
}: {
  first: string;
  last: string;
  className?: string;
}) {
  const hostRef = useRef<HTMLHeadingElement>(null);
  const scalerRef = useRef<HTMLSpanElement>(null);
  const w1Ref = useRef<HTMLSpanElement>(null);
  const w2Ref = useRef<HTMLSpanElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);
  const boundsRef = useRef<SVGRectElement>(null);
  const handleRefs = useRef<(SVGRectElement | null)[]>([]);
  const labelRef = useRef<SVGTextElement>(null);
  const pillRef = useRef<SVGRectElement>(null);
  const dimsRef = useRef<SVGTextElement>(null);

  const [geom, setGeom] = useState<Geom | null>(null);
  const [chrome, setChrome] = useState(false);
  const [out, setOut] = useState(false);
  const [serif, setSerif] = useState(false);
  const [mode, setMode] = useState<"off" | "hover" | "tap">("off");
  const [live, setLive] = useState(false);
  const [driver, setDriver] = useState<"perf" | "live">("perf");
  const [run, setRun] = useState(0);

  const perfDoneRef = useRef(false);
  const busyRef = useRef<null | "perf" | "live">(null);
  const posRef = useRef({ x: 0, y: 0 });
  const liveRaf = useRef(0);
  const hideTimer = useRef(0);
  const holdTimer = useRef(0);

  /* Selection bounds hug the ink, not the block: measured off the inline
     word spans, with Figma's near-zero side bearings and a little
     descender air below the surname's y. */
  const measure = (): Geom | null => {
    const host = hostRef.current;
    const w1 = w1Ref.current;
    const w2 = w2Ref.current;
    if (!host || !w1 || !w2) return null;
    const hostR = host.getBoundingClientRect();
    const r1 = w1.getBoundingClientRect();
    const r2 = w2.getBoundingClientRect();
    const lineH = r1.height;
    const bx = 3;
    return {
      w: hostR.width,
      h: hostR.height,
      selX: r1.left - hostR.left - bx,
      selY: r1.top - hostR.top + lineH * 0.08,
      selW: Math.max(r1.width, r2.width) + bx * 2,
      selH: r2.bottom - r1.top - lineH * 0.03,
    };
  };

  /* Same pointer taxonomy as the hover words: fine hovers, coarse taps,
     reduced motion opts out of everything. */
  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: no-preference)");
    const fine = window.matchMedia("(pointer: fine)");
    const coarse = window.matchMedia("(pointer: coarse)");
    const queries = [motion, fine, coarse];
    const update = () =>
      setMode(
        !motion.matches
          ? "off"
          : fine.matches
            ? "hover"
            : coarse.matches
              ? "tap"
              : "off",
      );
    update();
    queries.forEach((mq) => mq.addEventListener("change", update));
    return () => {
      queries.forEach((mq) => mq.removeEventListener("change", update));
      window.clearTimeout(hideTimer.current);
      window.clearTimeout(holdTimer.current);
      cancelAnimationFrame(liveRaf.current);
    };
  }, []);

  const track = (e: React.PointerEvent | React.MouseEvent) => {
    const host = hostRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();
    posRef.current.x = e.clientX - r.left;
    posRef.current.y = e.clientY - r.top;
  };

  /* The live selection, armed once the performance has bowed out: the
     visitor's own pointer wears Vik's cursor over the selected frame —
     the design hover word's exact contract, lerp lag included. */
  const engage = () => {
    const g = measure();
    if (!g) return;
    busyRef.current = "live";
    window.clearTimeout(hideTimer.current);
    setGeom(g);
    setRun((r) => r + 1);
    setChrome(true);
    setOut(false);
    setLive(true);
    setDriver("live");
    cancelAnimationFrame(liveRaf.current);
    let lx = posRef.current.x;
    let ly = posRef.current.y;
    const loop = () => {
      lx += (posRef.current.x - lx) * 0.3;
      ly += (posRef.current.y - ly) * 0.3;
      const cursor = cursorRef.current;
      if (cursor) {
        cursor.style.transform = `translate(${lx}px, ${ly}px)`;
        cursor.style.opacity = "1";
      }
      liveRaf.current = requestAnimationFrame(loop);
    };
    liveRaf.current = requestAnimationFrame(loop);
  };

  const disengage = () => {
    setLive(false);
    setOut(true);
    const cursor = cursorRef.current;
    if (cursor) cursor.style.opacity = "0";
    hideTimer.current = window.setTimeout(() => {
      cancelAnimationFrame(liveRaf.current);
      busyRef.current = null;
      setChrome(false);
      setOut(false);
      setGeom(null);
    }, EXIT_MS);
  };

  const onEnter = (e: React.PointerEvent) => {
    if (busyRef.current === "perf" || !perfDoneRef.current) return;
    track(e);
    engage();
  };
  const onLeave = () => {
    if (busyRef.current !== "live") return;
    disengage();
  };
  /* click, not pointerdown — a touch that turns into a scroll never lands
     here. Re-tapping mid-show restarts the pop and the hold. */
  const onTap = (e: React.MouseEvent) => {
    if (busyRef.current === "perf" || !perfDoneRef.current) return;
    track(e);
    window.clearTimeout(holdTimer.current);
    engage();
    holdTimer.current = window.setTimeout(disengage, 2400);
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const host = hostRef.current;
    if (!host) return;
    let raf = 0;
    let timer = 0;
    let disposed = false;

    const start = () => {
      const scaler = scalerRef.current;
      const g = measure();
      if (!scaler || !g) return;
      busyRef.current = "perf";
      setDriver("perf");
      const hx = g.selX + g.selW;
      const hy = g.selY + g.selH;
      const corners: Array<[number, number]> = [
        [g.selX, g.selY],
        [hx, g.selY],
        [g.selX, hy],
        [hx, hy],
      ];

      /* Cursor waypoints: in from the portrait side, an arc down onto the
         surname, small drifts while deciding, then the corner handle and
         out top-right. The damped follow below rounds every corner. */
      const wp: Waypoint[] = [
        { t: 0, x: g.w + 70, y: hy - g.selH * 0.3 },
        { t: 640, x: g.selW * 0.8, y: g.selY + g.selH * 0.16, ease: easeOut },
        {
          t: T.click,
          x: g.selW * 0.42,
          y: g.selY + g.selH * 0.72,
          ease: easeInOut,
        },
        {
          t: T.tryOn - 140,
          x: g.selW * 0.33,
          y: g.selY + g.selH * 0.64,
          ease: easeInOut,
        },
        {
          t: T.tryOff + 120,
          x: g.selW * 0.4,
          y: g.selY + g.selH * 0.58,
          ease: easeInOut,
        },
        { t: T.grab, x: hx, y: hy, ease: easeInOut },
        { t: T.deselect, x: hx, y: hy },
        { t: T.done - 60, x: g.w + 90, y: g.selY - 60, ease: easeInOut },
      ];
      const pathAt = (t: number): [number, number] => {
        for (let i = 0; i < wp.length - 1; i++) {
          const a = wp[i];
          const b = wp[i + 1];
          if (t <= b.t) {
            const u = (b.ease ?? easeInOut)(seg(t, a.t, b.t));
            return [a.x + (b.x - a.x) * u, a.y + (b.y - a.y) * u];
          }
        }
        const l = wp[wp.length - 1];
        return [l.x, l.y];
      };

      const dims0 = `${Math.round(g.selW)} × ${Math.round(g.selH)}`;
      const pillW = dims0.length * 6.4 + 13;

      setGeom(g);
      const t0 = performance.now();
      let px = wp[0].x;
      let py = wp[0].y;
      let lastNow = t0;
      let lastS = 1;
      let lastDims = dims0;
      const phase = { chrome: false, out: false, serif: false };

      const finish = () => {
        scaler.style.transform = "";
        perfDoneRef.current = true;
        busyRef.current = null;
        setGeom(null);
        setChrome(false);
        setOut(false);
        setSerif(false);
      };

      const loop = (now: number) => {
        const t = now - t0;
        const dt = Math.min(48, now - lastNow);
        lastNow = now;
        if (t >= T.done) {
          finish();
          return;
        }

        if (!phase.chrome && t >= T.click) {
          phase.chrome = true;
          setChrome(true);
        }
        const wantSerif = t >= T.tryOn && t < T.tryOff;
        if (phase.serif !== wantSerif) {
          phase.serif = wantSerif;
          setSerif(wantSerif);
        }
        if (!phase.out && t >= T.deselect) {
          phase.out = true;
          setOut(true);
        }

        /* the frame under drag: text scales about the same origin the
           chrome math uses, so bounds, handles and badge keep the hug */
        const s = scaleAt(t);
        scaler.style.transform = s === 1 ? "" : `scale(${s})`;
        if (s !== lastS) {
          lastS = s;
          boundsRef.current?.setAttribute("x", String(g.selX * s));
          boundsRef.current?.setAttribute("y", String(g.selY * s));
          boundsRef.current?.setAttribute("width", String(g.selW * s));
          boundsRef.current?.setAttribute("height", String(g.selH * s));
          handleRefs.current.forEach((el, i) => {
            el?.setAttribute("x", String(corners[i][0] * s - HANDLE / 2));
            el?.setAttribute("y", String(corners[i][1] * s - HANDLE / 2));
          });
          labelRef.current?.setAttribute("x", String(g.selX * s));
          labelRef.current?.setAttribute("y", String(g.selY * s - 8));
          const cx = (g.selX + g.selW / 2) * s;
          const pillY = hy * s + 5;
          pillRef.current?.setAttribute("x", String(cx - pillW / 2));
          pillRef.current?.setAttribute("y", String(pillY));
          const dimsEl = dimsRef.current;
          if (dimsEl) {
            dimsEl.setAttribute("x", String(cx));
            dimsEl.setAttribute("y", String(pillY + 13));
            const dims = `${Math.round(g.selW * s)} × ${Math.round(g.selH * s)}`;
            if (dims !== lastDims) {
              lastDims = dims;
              dimsEl.textContent = dims;
            }
          }
        }

        /* the cursor: eased waypoint target + damped follow = round
           corners and human arrival; a faint drift while it "thinks";
           locked to the handle while dragging. */
        const dragging = t >= T.grab && t < T.deselect;
        let [tx, ty] = dragging ? [hx * s, hy * s] : pathAt(t);
        if (!dragging && t > T.click + 150 && t < T.grab - 200) {
          tx += Math.sin(t * 0.0031) * 2.4;
          ty += Math.cos(t * 0.0023) * 1.8;
        }
        const k = dragging ? 0.5 : 0.22;
        const follow = 1 - (1 - k) ** (dt / 16.7);
        px += (tx - px) * follow;
        py += (ty - py) * follow;
        const cursor = cursorRef.current;
        if (cursor) {
          cursor.style.transform = `translate(${px}px, ${py}px) scale(${dipAt(t)})`;
          const fadeIn = Math.min(1, t / 260);
          const fadeOut =
            t >= T.deselect ? 1 - seg(t, T.deselect, T.done - 80) : 1;
          cursor.style.opacity = String(Math.max(0, Math.min(fadeIn, fadeOut)));
        }
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        document.fonts.ready.then(() => {
          if (disposed) return;
          timer = window.setTimeout(start, 600);
        });
      },
      { threshold: 0.5 },
    );
    io.observe(host);
    return () => {
      disposed = true;
      io.disconnect();
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, []);

  const dims0 = geom
    ? `${Math.round(geom.selW)} × ${Math.round(geom.selH)}`
    : "";
  const pillW = dims0.length * 6.4 + 13;
  const pillX = geom ? geom.selX + geom.selW / 2 - pillW / 2 : 0;
  const pillY = geom ? geom.selY + geom.selH + 5 : 0;

  return (
    <h1
      ref={hostRef}
      className={`${className ?? ""} ${styles.host} ${inter.variable}`}
      data-hot={live ? "1" : "0"}
    >
      <span
        ref={scalerRef}
        className={styles.scaler}
        onPointerEnter={mode === "hover" ? onEnter : undefined}
        onPointerMove={mode === "hover" ? track : undefined}
        onPointerLeave={mode === "hover" ? onLeave : undefined}
        onClick={mode === "tap" ? onTap : undefined}
      >
        <span className={styles.line}>
          <span ref={w1Ref}>{first}</span>
        </span>{" "}
        <span className={styles.line} data-face={serif ? "serif" : undefined}>
          <span ref={w2Ref}>{last}</span>
        </span>
      </span>
      {geom && chrome && (
        <span
          key={run}
          className={hw.overlay}
          data-out={out ? "1" : "0"}
          aria-hidden
        >
          <svg className={hw.svg} viewBox={`0 0 ${geom.w} ${geom.h}`}>
            <rect
              ref={boundsRef}
              className={hw.figBounds}
              x={geom.selX}
              y={geom.selY}
              width={geom.selW}
              height={geom.selH}
            />
            {(
              [
                [geom.selX, geom.selY],
                [geom.selX + geom.selW, geom.selY],
                [geom.selX, geom.selY + geom.selH],
                [geom.selX + geom.selW, geom.selY + geom.selH],
              ] as const
            ).map(([cx, cy], i) => (
              <rect
                key={i}
                ref={(el) => {
                  handleRefs.current[i] = el;
                }}
                className={hw.figHandle}
                x={cx - HANDLE / 2}
                y={cy - HANDLE / 2}
                width={HANDLE}
                height={HANDLE}
                style={{ "--i": i } as React.CSSProperties}
              />
            ))}
            <text
              ref={labelRef}
              className={hw.figLabel}
              x={geom.selX}
              y={geom.selY - 8}
            >
              viskhan-khasiyev
            </text>
            <g className={hw.figDims}>
              <rect
                ref={pillRef}
                className={hw.figDimsBg}
                x={pillX}
                y={pillY}
                width={pillW}
                height={18}
                rx={2}
              />
              <text
                ref={dimsRef}
                className={hw.figDimsText}
                x={pillX + pillW / 2}
                y={pillY + 13}
                textAnchor="middle"
              >
                {dims0}
              </text>
            </g>
          </svg>
        </span>
      )}
      {geom && (
        <span
          ref={cursorRef}
          className={styles.cursor}
          data-mode={driver}
          aria-hidden
        >
          <svg width="24" height="36" viewBox="0 0 24 36" fill="none">
            <path
              d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19841L11.7841 12.3673H5.65376Z"
              fill="var(--fig)"
              stroke="#fff"
              strokeWidth="1"
            />
          </svg>
          <FigTag className={hw.figTag} />
        </span>
      )}
    </h1>
  );
}
